import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { PersonalShadowAiStats } from '@kirin/api-client/contracts/account-shadow-ai-stats';
import type {
  BrowserAgentSiteUsersQuery,
  BrowserAgentSiteUsersResponse,
  SetBrowserAgentDecisionRequest,
  ShadowAiSuccessResponse,
  ShadowAiToolUsersRowFilter,
} from '@kirin/api-client/contracts/shadow-ai';
import {
  listOrganizationShadowAiTools,
  listUserShadowAiTools,
  listOrganizationRunningProcesses,
  listUserRunningProcesses,
  getShadowAiToolUsers,
  getOrgShadowAiStats,
  getPersonalShadowAiStats,
  listOrgNetworkConnections,
  listOrgNetworkDomains,
  getNetworkConnectionUsers,
  getNetworkDomainHosts,
  setNetworkDomainHostDecision,
  getOrgNetworkStats,
  getAgentNames,
  getOrgTopShadowAiUsers,
  getOrgTopShadowAiTools,
  getBrowserAgentSiteUsers,
  setBrowserAgentDecision,
  type ListShadowAiToolsParams,
  type ListRunningProcessesParams,
  type ListNetworkConnectionsParams,
} from '@kirin/api-client/api/shadow-ai';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';
import { getErrorMessage } from './errorHelpers';

export const SHADOW_AI_REFRESH_INTERVAL = 30_000;
export const RUNNING_PROCESSES_REFRESH_INTERVAL = 5_000;

export function useUserInstalledTools(
  organizationId: string | undefined,
  userId: string | null,
  enabled: boolean = true
) {
  return useQuery<
    Array<{
      id: string;
      name: string;
      category: string;
      lastSeenAt: string;
    }>,
    Error
  >({
    queryKey: queryKeys.shadowAi.userInstalledTools(organizationId, userId),
    queryFn: async () => {
      if (!organizationId || !userId) {
        throw new Error('organizationId and userId are required');
      }
      const response = await listOrganizationShadowAiTools(
        apiClient,
        organizationId,
        {
          userId,
          sourceType: 'installed',
          limit: 100,
        }
      );
      return response.tools.map(tool => ({
        id: tool.toolId,
        name: tool.name,
        category: tool.category,
        lastSeenAt: tool.lastUsed ?? tool.addedAt ?? new Date().toISOString(),
      }));
    },
    enabled: enabled && !!organizationId && !!userId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useUserBrowserTools(
  organizationId: string | undefined,
  userId: string | null,
  enabled: boolean = true
) {
  return useQuery<
    Array<{
      id: string;
      domain: string;
      name: string;
      category: string;
      lastSeenAt: string;
    }>,
    Error
  >({
    queryKey: queryKeys.shadowAi.userBrowserTools(organizationId, userId),
    queryFn: async () => {
      if (!organizationId || !userId) {
        throw new Error('organizationId and userId are required');
      }
      const response = await listOrganizationShadowAiTools(
        apiClient,
        organizationId,
        {
          userId,
          sourceType: 'browser',
          limit: 100,
        }
      );
      return response.tools.map(tool => ({
        id: tool.toolId,
        domain: tool.toolId, // For browser tools, toolId contains the actual domain (e.g., "chat.openai.com")
        name: tool.name,
        category: tool.category,
        lastSeenAt: tool.lastUsed ?? tool.addedAt ?? new Date().toISOString(),
      }));
    },
    enabled: enabled && !!organizationId && !!userId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * The users behind one Browser Agent Sites row (KIR-4704 review #15) — the
 * Users count drill-down. Keyed under the view's prefix, so a decision's
 * invalidation refreshes it with the table.
 */
export function useBrowserAgentSiteUsers(
  organizationId: string | undefined,
  site: {
    host: string;
    agentType: BrowserAgentSiteUsersQuery['agentType'];
  } | null,
  enabled: boolean = true
): UseQueryResult<BrowserAgentSiteUsersResponse, Error> {
  return useQuery<BrowserAgentSiteUsersResponse, Error>({
    queryKey: [
      ...queryKeys.shadowAi.browserAgentSites(organizationId),
      'users',
      site?.agentType,
      site?.host,
    ],
    queryFn: () => {
      if (!organizationId || !site) {
        throw new Error('organizationId and site are required');
      }
      return getBrowserAgentSiteUsers(apiClient, organizationId, site);
    },
    enabled: enabled && !!organizationId && !!site,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Set (or clear) a Browser Agent Site's enforcement decision (KIR-4704). On
 * success, refetches the site view so Enforcement + Approval update.
 */
export function useSetBrowserAgentDecision(
  organizationId: string | undefined
): UseMutationResult<
  ShadowAiSuccessResponse,
  unknown,
  SetBrowserAgentDecisionRequest
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (body: SetBrowserAgentDecisionRequest) => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return setBrowserAgentDecision(apiClient, organizationId, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.shadowAi.browserAgentSites(organizationId),
      });
    },
    onError: error => {
      toast({
        variant: 'destructive',
        title: 'Failed to update site decision',
        description: getErrorMessage(error),
      });
    },
  });
}

export function useShadowAiTools(
  organizationId: string | undefined,
  params: ListShadowAiToolsParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.list(organizationId, params),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrganizationShadowAiTools(apiClient, organizationId, params);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    placeholderData: prev => prev,
  });
}

export function useUserShadowAiTools(
  params: ListShadowAiToolsParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.personal(params),
    queryFn: () => {
      return listUserShadowAiTools(apiClient, params);
    },
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    placeholderData: prev => prev,
  });
}

export function useRunningProcesses(
  organizationId: string | undefined,
  params: ListRunningProcessesParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.running(organizationId, params),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrganizationRunningProcesses(
        apiClient,
        organizationId,
        params
      );
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
  });
}

export function useUserRunningProcesses(
  params: ListRunningProcessesParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.runningPersonal(params),
    queryFn: () => {
      return listUserRunningProcesses(apiClient, params);
    },
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: RUNNING_PROCESSES_REFRESH_INTERVAL,
    placeholderData: prev => prev,
  });
}

export function useToolUsers(
  organizationId: string | undefined,
  toolId: string | undefined,
  enabled: boolean = true,
  /**
   * KIR-3495: narrow the expanded row's user list to one org member, so the
   * Shadow AI user-scope page does not list the whole org under a tool.
   */
  scopeUserId?: string,
  /** KIR-5296: list only one table row's users (its category + tab). */
  rowFilter?: ShadowAiToolUsersRowFilter
) {
  const narrowed = scopeUserId || rowFilter?.category || rowFilter?.source;
  return useQuery({
    queryKey: narrowed
      ? [
          ...queryKeys.shadowAi.toolUsers(organizationId, toolId),
          {
            scopeUserId,
            category: rowFilter?.category,
            source: rowFilter?.source,
          },
        ]
      : queryKeys.shadowAi.toolUsers(organizationId, toolId),
    queryFn: () => {
      if (!organizationId || !toolId) {
        throw new Error('organizationId and toolId are required');
      }
      return getShadowAiToolUsers(
        apiClient,
        organizationId,
        toolId,
        scopeUserId,
        rowFilter
      );
    },
    enabled: enabled && !!organizationId && !!toolId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useShadowAiOrgStats(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.stats(organizationId),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgShadowAiStats(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
}

// Gate lives in the component; the hook only forwards `enabled` to useQuery.
export function useUserShadowAiStats(
  enabled: boolean = true
): UseQueryResult<PersonalShadowAiStats, unknown> {
  // Key on identity so a switched account never reads the prior user's cache.
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.shadowAi.personalStats(user?.email),
    queryFn: () => getPersonalShadowAiStats(apiClient),
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
}

export function useOrgNetworkConnections(
  organizationId: string | undefined,
  params: ListNetworkConnectionsParams = {},
  enabled: boolean = true
) {
  // Exclude knownTotal and countScope from the query key — both are
  // request-shape perf hints (they tell the backend whether to skip COUNT)
  // and would otherwise spawn a new cache entry on every render where the
  // hint changes, defeating the optimization.
  const { knownTotal, countScope, ...queryKeyParams } = params;
  void knownTotal;
  void countScope;
  return useQuery({
    queryKey: queryKeys.shadowAi.networkConnections(
      organizationId,
      queryKeyParams
    ),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrgNetworkConnections(apiClient, organizationId, params);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    // Pause the 30s poll when the tab is in the background — the data is
    // not visible and the join-heavy network endpoints are the most
    // expensive in this view.
    refetchIntervalInBackground: false,
    placeholderData: prev => prev,
  });
}

/**
 * The per-user attributions behind one expanded row.
 *
 * `host` narrows a GROUPED row to one of its member hosts (KIR-4948). Omitted,
 * the answer spans the whole service, which is right for the collapsed row and
 * wrong for a per-host panel: a service rolling up 24 hosts would otherwise
 * list users who never touched the host on screen.
 */
export function useNetworkConnectionUsers(
  organizationId: string | undefined,
  domain: string | null,
  enabled: boolean = true,
  processName?: string | null,
  host?: string | null
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkConnectionUsers(
      organizationId,
      domain,
      processName,
      host
    ),
    queryFn: () => {
      if (!organizationId || !domain) {
        throw new Error('organizationId and domain are required');
      }
      return getNetworkConnectionUsers(apiClient, organizationId, domain, {
        ...(processName ? { processName } : {}),
        ...(host ? { host } : {}),
      });
    },
    enabled: enabled && !!organizationId && !!domain,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * KIR-4838: the hosts behind one grouped Network Discovery row.
 *
 * Deliberately lazy — `enabled` is driven by the row's expanded state, matching
 * `useNetworkConnectionUsers`, so the list page never pays for hosts nobody
 * opens.
 */
export function useNetworkDomainHosts(
  organizationId: string | undefined,
  domain: string | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkDomainHosts(organizationId, domain),
    queryFn: () => {
      if (!organizationId || !domain) {
        throw new Error('organizationId and domain are required');
      }
      return getNetworkDomainHosts(apiClient, organizationId, domain);
    },
    enabled: enabled && !!organizationId && !!domain,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * KIR-4838 PR D: set or clear one host's decision override.
 *
 * `decision: null` resets the host to inheriting its service — a domain-level
 * action deliberately leaves overrides alone, so this is the only way back.
 * Invalidates the hosts list and the connections list, because the grouped
 * row's status can change when a host stops dissenting from it.
 */
export function useSetNetworkDomainHostDecision(
  organizationId: string | undefined,
  domain: string | null
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      host,
      decision,
    }: {
      host: string;
      decision: 'identified' | 'dismissed' | null;
    }) => {
      if (!organizationId || !domain) {
        throw new Error('organizationId and domain are required');
      }
      return setNetworkDomainHostDecision(
        apiClient,
        organizationId,
        domain,
        host,
        {
          decision,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shadowAi.networkDomainHosts(organizationId, domain),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.shadowAi.all });
    },
    // A host that has not been rolled up yet answers 404, and a non-manager
    // answers 403. Without this the button simply stopped being disabled and
    // the row looked unchanged, which reads as "the click did nothing" rather
    // than as a failure. Every other triage mutation in this tab toasts.
    onError: error => {
      toast({
        variant: 'destructive',
        title: 'Failed to update host decision',
        description: getErrorMessage(error),
      });
    },
  });
}

export function useOrgNetworkStats(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkStats(organizationId),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgNetworkStats(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    // KIR-5131: polled every 30s like the domains table, and fresh for as
    // long. The shared config (staleTime 0 + refetch on focus) re-ran these
    // whole-organization aggregates on every tab switch and remount, on top
    // of the poll. The table already opts out the same way; the user's own
    // triage still refreshes both at once through the shadowAi.all
    // invalidation, which ignores staleTime for mounted queries.
    staleTime: SHADOW_AI_REFRESH_INTERVAL,
    refetchOnWindowFocus: false,
    retry: retryHandlers.list,
    refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    refetchIntervalInBackground: false,
  });
}

export function useAgentNames(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['shadowAi', 'agentNames', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getAgentNames(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgNetworkDomains(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.shadowAi.networkDomains(organizationId),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrgNetworkDomains(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgTopShadowAiUsers(
  organizationId: string | undefined,
  options: { limit?: number } = {},
  enabled: boolean = true
) {
  const { limit = 10 } = options;

  return useQuery({
    queryKey: queryKeys.shadowAi.topUsers(organizationId, limit),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgTopShadowAiUsers(apiClient, organizationId, { limit });
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

export function useOrgTopShadowAiTools(
  organizationId: string | undefined,
  options: { limit?: number; sourceType?: 'installed' | 'browser' } = {},
  enabled: boolean = true
) {
  const { limit = 10, sourceType } = options;

  return useQuery({
    queryKey: queryKeys.shadowAi.topTools(organizationId, limit, sourceType),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return getOrgTopShadowAiTools(apiClient, organizationId, {
        limit,
        sourceType,
      });
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}
